import { useContext } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import {
  Button, Form, ModalContext, SidebarModal, SidebarNoticeBox,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import { confirmDelete } from '@functions/entity'

import useAgencyContact, { useAgencyContactForm } from '@hooks/useAgencyContact'

import type { ActionResult } from '@redux/modules/module'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import type { EntitiesState } from '@redux/entities'

import type { UseAgencyContactPayload } from '@hooks/useAgencyContact'

import type { AgencyContactModel } from '@models/types'

const MODAL_KEY = 'CreateOrEditAgencyContactModal'

const getContactTypeDescription = (contactType: 'billing' | 'principal' | 'publication') => {
  const descriptions = {
    billing: `Billing contacts will receive automated system invoices,
    and any correspondence from our teams regarding accounting changes, features and potential issues.`,

    principal: `A principal contact is required for Realhub to contact
    the agency with important matters. If a principal contact is not added
    we can’t guarantee privacy around sensitive matters.`,

    publication: `Publication contacts will be used to communicate booking,
    artwork and newspaper issues as well as send reminders and alerts to
    make sure you never miss a planned publication marketing campaign.`,
  }

  return descriptions[contactType] || ''
}

type ModalCallbacks = {
  closeModal: () => void,
  createAgencyContact: (payload: HandleCallbackActionParams) => Promise<ActionResult<AgencyContactModel>>,
  updateAgencyContact: (payload: HandleCallbackActionParams) => Promise<ActionResult<AgencyContactModel>>,
}

type CreateOrUpdateAgencyContactParams = {
  callbacks: ModalCallbacks,
  createFn: UseAgencyContactPayload['callbacks']['createAgencyContact'],
  entityState: EntitiesState['agencyContacts'],
  updateFn: UseAgencyContactPayload['callbacks']['updateAgencyContact'],
}

const createOrUpdateAgencyContact = (params: CreateOrUpdateAgencyContactParams) => {
  const {
    entityState, callbacks, createFn, updateFn,
  } = params

  const { closeModal, createAgencyContact, updateAgencyContact } = callbacks

  const text = entityState.id ? 'Contact Updated' : 'Contact Created'
  const actionFn = entityState.id ? updateFn : createFn

  const agencyContactPayload = {
    callbacks: {
      action: actionFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: text,
  }

  if (entityState.id) return updateAgencyContact(agencyContactPayload)

  return createAgencyContact(agencyContactPayload)
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateOrEditAgencyContactModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal } = callbacks

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { agencyContact: initAgencyContact } = modalPayload

  const agencyContactPayload = useAgencyContact(initAgencyContact)
  const {
    agencyContact,
    callbacks: {
      createAgencyContact: createFn,
      deleteAgencyContact: deleteFn,
      updateAgencyContact: updateFn,
    },
    creating,
    deleting,
    updating,
  } = agencyContactPayload

  const agencyContactFormPayload = useAgencyContactForm(agencyContact, { validateOn: 'change' })
  const {
    entityState,
    entityState: {
      contact_type,
      email,
      id,
      name,
      phone,
    },
    errors,
    handlers,
    saveEnabled,
  } = agencyContactFormPayload

  const confirmDeleteParams = {
    callbacks: {
      afterAction: closeModal,
      deleteFn,
    },
    strings: {
      deleteString: 'delete',
      entityName: 'Contact',
    },
  }

  const updateOrCreateButtonText = id ? 'Save Contact' : 'Create Contact'

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal} size="small">
      <SidebarModal.Header callbacks={callbacks} title={id ? 'Edit' : 'Add'} titleSecondLine="Agency Contact" />

      <SidebarModal.Content>
        <Form>
          <Form.Field errorMessage={errors.contact_type} label="* Contact Type">
            {/*
             // @ts-expect-error TS2322 */}
            <select
              name="contact_type"
              value={contact_type}
              {...handlers}
            >
              <option value="">Select Contact Type</option>
              <option value="billing">Billing</option>
              <option value="principal">Principal</option>
              <option value="publication">Publication</option>
            </select>
          </Form.Field>

          {!!contact_type && (
            <SidebarNoticeBox boxProps={{ marginBottom: 'none', marginTop: 'large' }}>
              <SidebarNoticeBox.Title>What is a <strong>{contact_type}</strong> contact?</SidebarNoticeBox.Title>

              <SidebarNoticeBox.Paragraph>
                {getContactTypeDescription(contact_type)}
              </SidebarNoticeBox.Paragraph>
            </SidebarNoticeBox>
          )}

          <Form.Field errorMessage={errors.name} label="* Name" marginTop="large">
            <input name="name" type="text" value={name} {...handlers} />
          </Form.Field>

          <Form.Field errorMessage={errors.email} label="* Email Address" marginTop="large">
            <input name="email" type="text" value={email} {...handlers} />
          </Form.Field>

          <Form.Field errorMessage={errors.phone} label="* Phone" marginTop="large">
            <input name="phone" type="text" value={phone} {...handlers} />
          </Form.Field>

          {id && (
            <Form.Field label="More Options" marginTop="large">
              <Button
                buttonStyle="secondaryUtility"
                icon={<FontAwesomeIcon icon={faTrashAlt} />}
                loading={deleting}
                // @ts-expect-error TS2345
                onClick={() => confirmDelete(confirmDeleteParams)}
                size="medium"
              >
                Delete Contact
              </Button>
            </Form.Field>
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          onClick={() => createOrUpdateAgencyContact({
            entityState, callbacks, createFn, updateFn,
          })}
          size="large"
        >
          {saveEnabled ? updateOrCreateButtonText : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditAgencyContactModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
